export const clampNumber = (value, min, max) => {
  if (!Number.isFinite(value)) return min;
  return Math.max(min, Math.min(max, value));
};

const safeHypot = (x, y) => {
  x = Number.isFinite(x) ? x : 0;
  y = Number.isFinite(y) ? y : 0;
  return Math.hypot(x, y);
};

const normalize = (vector) => {
  const length = safeHypot(vector.x, vector.y);
  if (!length) return { x: 0, y: 0, length: 0 };
  return { x: vector.x / length, y: vector.y / length, length };
};

const dot = (a, b) => a.x * b.x + a.y * b.y;

const angleBetween = (a, b) => {
  const normA = normalize(a);
  const normB = normalize(b);
  if (!normA.length || !normB.length) return 0;
  const cosine = clampNumber(dot(normA, normB), -1, 1);
  return Math.acos(cosine);
};

const sigmoid = (value) => 1 / (1 + Math.exp(-value));

const defaultGoal = { x: 420, yTop: -65, yBottom: 65 };

export const SHOT_FEATURE_CONFIG = [
  { key: 'angleQuality', weight: 1.35 },
  { key: 'distanceQuality', weight: 1.55 },
  { key: 'speedQuality', weight: 0.6 },
  { key: 'spaceQuality', weight: 0.95 },
  { key: 'supportQuality', weight: 0.45 },
  { key: 'pressureComposite', weight: -1.25 },
  { key: 'lanePenalty', weight: -1.05 },
  { key: 'distancePenalty', weight: -2.1 }
];

export const SHOT_LOGIT_BIAS = -1.6;

// Keep the feature calculation versioned.  Replays can outlive the code that
// produced them, so callers should persist this value beside a recorded xG.
// The legacy `computeShotXg` output remains unchanged; new consumers can opt in
// to the calibrated pre-shot value returned by `computeCalibratedShotXg`.
export const SHOT_MODEL_VERSION = 'xg-preshoot-v2';

// Candidate Platt calibration fitted from the checked-in replay audit.  This is
// deliberately opt-in: calibration must be re-fit when the replay corpus or
// the feature model changes.  Keeping the coefficients here gives the UI and
// offline evaluation code one stable, serialisable hook instead of embedding
// calibration in individual callers.
export const XG_CALIBRATION_CANDIDATE = Object.freeze({
  version: 'v8-full-replay-candidate',
  intercept: -1.51804531,
  slope: 0.34060414
});

const LOGIT_EPSILON = 1e-6;

const logit = probability => {
  const p = clampNumber(probability, LOGIT_EPSILON, 1 - LOGIT_EPSILON);
  return Math.log(p / (1 - p));
};

/**
 * Apply a monotonic logistic (Platt) calibration to an xG probability.
 *
 * `calibration` may be null to request an identity transform.  A non-positive
 * slope is rejected because it would reverse the model's ranking of chances.
 * Invalid values are also handled as identity transforms so a malformed
 * replay record can never produce NaN xG.
 */
export function calibrateShotProbability(probability, calibration = null) {
  const raw = clampNumber(probability, 0, 1);
  if (!calibration || typeof calibration !== 'object') return raw;
  const intercept = Number(calibration.intercept);
  const slope = Number(calibration.slope);
  if (!Number.isFinite(intercept) || !Number.isFinite(slope) || slope <= 0) return raw;
  return clampNumber(sigmoid(intercept + slope * logit(raw)), 0, 1);
}

const idealSpeedFromContext = (stadium) => {
  if (stadium && stadium.playerPhysics && Number.isFinite(stadium.playerPhysics.kickStrength)) {
    return clampNumber(stadium.playerPhysics.kickStrength * 1.1, 3.5, 7);
  }
  return 5;
};

const finiteCoordinate = (value, fallback = 0) => Number.isFinite(Number(value)) ? Number(value) : fallback;

const entityPosition = entity => {
  const position = entity?.position || entity;
  return {
    x: finiteCoordinate(position?.x),
    y: finiteCoordinate(position?.y)
  };
};

const entityVelocity = entity => {
  const velocity = entity?.velocity || entity;
  return {
    x: finiteCoordinate(velocity?.x),
    y: finiteCoordinate(velocity?.y)
  };
};

const entityRadius = (entity, fallback = 15) => {
  const radius = Number(entity?.radius ?? entity?.collisionRadius ?? entity?.physics?.radius);
  return Number.isFinite(radius) && radius > 0 ? clampNumber(radius, 1, 60) : fallback;
};

/**
 * Estimate how much of the actual goal mouth is hidden by players on the
 * shot's line.  This is a geometric visibility feature, not a goal outcome
 * feature: it is therefore safe to use for pre-shot xG.
 */
const goalVisibility = (ball, goal, pathDir, opponents) => {
  const goalHeight = Math.max(1, Math.abs(goal.yBottom - goal.yTop));
  const distanceToGoal = Math.max(0.0001, pathDir.length);
  const intervals = [];
  for (let i = 0; i < opponents.length; i += 1) {
    const opponent = opponents[i];
    const position = entityPosition(opponent);
    const offset = { x: position.x - ball.x, y: position.y - ball.y };
    const projection = dot(offset, pathDir);
    if (projection <= 0 || projection >= distanceToGoal * 1.05) continue;
    const lateral = pathDir.x * offset.y - pathDir.y * offset.x;
    const radius = entityRadius(opponent) + 10;
    // Project the player's collision disc onto the goal plane.  Clamping keeps
    // a player very close to the ball from turning a finite goal into an
    // infinite interval when replay coordinates are noisy.
    const scale = clampNumber(distanceToGoal / Math.max(projection, 8), 1, 18);
    const centerAtGoal = (goal.yTop + goal.yBottom) / 2 + lateral * scale;
    const halfAtGoal = clampNumber(radius * scale, 0, goalHeight * 1.5);
    const start = Math.max(goal.yTop, centerAtGoal - halfAtGoal);
    const end = Math.min(goal.yBottom, centerAtGoal + halfAtGoal);
    if (end > start) intervals.push([start, end]);
  }
  intervals.sort((a, b) => a[0] - b[0]);
  let blocked = 0;
  let current = null;
  intervals.forEach(interval => {
    if (!current) {
      current = interval;
      return;
    }
    if (interval[0] <= current[1]) {
      current[1] = Math.max(current[1], interval[1]);
    } else {
      blocked += current[1] - current[0];
      current = interval;
    }
  });
  if (current) blocked += current[1] - current[0];
  const blockedFraction = clampNumber(blocked / goalHeight, 0, 1);
  return {
    blockedFraction,
    visibleFraction: 1 - blockedFraction,
    intervals: intervals.length
  };
};

/**
 * Approximate the probability that a defender can reach the shot corridor
 * before the ball arrives.  It intentionally uses a soft score rather than a
 * binary collision test because replay ticks and velocities are quantised.
 */
const estimateInterceptionRisk = (ball, pathDir, distanceToGoal, shotSpeed, opponents) => {
  const travelSpeed = Math.max(0.25, shotSpeed);
  const ballArrival = distanceToGoal / travelSpeed;
  let risk = 0;
  for (let i = 0; i < opponents.length; i += 1) {
    const opponent = opponents[i];
    const position = entityPosition(opponent);
    const offset = { x: position.x - ball.x, y: position.y - ball.y };
    const projection = dot(offset, pathDir);
    if (projection <= 0 || projection >= distanceToGoal) continue;
    const lateral = Math.abs(pathDir.x * offset.y - pathDir.y * offset.x);
    const corridorRadius = entityRadius(opponent) + 10;
    if (lateral > corridorRadius * 4) continue;
    const speed = Math.max(0.75, safeHypot(entityVelocity(opponent).x, entityVelocity(opponent).y));
    const reachTime = safeHypot(projection, lateral) / speed;
    const timing = sigmoid((ballArrival - reachTime) / 1.5);
    const laneWeight = Math.exp(-0.5 * Math.pow(lateral / Math.max(corridorRadius, 1), 2));
    risk += timing * laneWeight;
  }
  return clampNumber(1 - Math.exp(-risk), 0, 1);
};

const buildSupportStats = (entities, reference, pathDir, distanceLimit, pathWidth) => {
  let laneCount = 0;
  let closeCount = 0;
  for (let i = 0; i < entities.length; i++) {
    const entity = entities[i];
    const position = entityPosition(entity);
    const offset = {
      x: position.x - reference.x,
      y: position.y - reference.y
    };
    const dist = safeHypot(offset.x, offset.y);
    if (!dist) continue;
    const proj = dot(offset, pathDir);
    if (proj > -40 && dist <= distanceLimit) closeCount += 1;
    if (proj >= 0 && proj <= distanceLimit) {
      const lateralSquared = Math.max(0, dist * dist - proj * proj);
      if (lateralSquared <= pathWidth * pathWidth) laneCount += 1;
    }
  }
  return { laneCount, closeCount };
};

export function buildShotFeatureBundle(context) {
  if (!context) {
    return {
      logisticFeatures: {},
      detailFeatures: {},
      meta: {
        modelVersion: SHOT_MODEL_VERSION,
        distanceToGoal: 0,
        laneBlockers: 0,
        supportLane: 0,
        supportClose: 0,
        nearestOpponent: 0,
        closeOpponents: 0,
        goalVisibleFraction: 1,
        interceptionRisk: 0
      }
    };
  }

  const rawGoal = context.goal || defaultGoal;
  const goal = {
    x: finiteCoordinate(rawGoal.x, defaultGoal.x),
    yTop: finiteCoordinate(rawGoal.yTop, defaultGoal.yTop),
    yBottom: finiteCoordinate(rawGoal.yBottom, defaultGoal.yBottom)
  };
  const rawBall = context.ball || {};
  const ball = {
    x: finiteCoordinate(rawBall.x),
    y: finiteCoordinate(rawBall.y),
    vx: finiteCoordinate(rawBall.vx),
    vy: finiteCoordinate(rawBall.vy)
  };
  const rawShooter = context.shooter || {};
  const shooterPosition = entityPosition(rawShooter.position || rawShooter);
  const shooter = {
    ...rawShooter,
    position: shooterPosition,
    velocity: entityVelocity(rawShooter.velocity || rawShooter)
  };
  const opponents = Array.isArray(context.opponents) ? context.opponents : [];
  const teammates = Array.isArray(context.teammates) ? context.teammates : [];
  const stadium = context.stadium || {};

  const goalCenterY = (goal.yTop + goal.yBottom) / 2;
  const goalHeight = Math.abs(goal.yBottom - goal.yTop) || 120;
  const toCenter = { x: goal.x - ball.x, y: goalCenterY - ball.y };
  const pathDir = normalize(toCenter);
  const distanceToGoal = pathDir.length || 0.0001;
  const fieldWidth = Number.isFinite(stadium.width) ? Math.abs(stadium.width) : 800;
  const maxDistance = Math.max(fieldWidth * 0.9, goalHeight * 2);
  const idealSpeed = idealSpeedFromContext(stadium);
  // Release velocity is outcome/execution information and leaks the answer if
  // used while training a pre-shot model.  The pre-shot path substitutes the
  // configured kick strength unless the caller explicitly supplied a velocity
  // measured before the kick.
  const speed = context.preShot && !context.hasPreKickVelocity
    ? idealSpeed
    : safeHypot(ball.vx, ball.vy);

  const topVec = { x: goal.x - ball.x, y: goal.yTop - ball.y };
  const bottomVec = { x: goal.x - ball.x, y: goal.yBottom - ball.y };
  const angleSpan = angleBetween(topVec, bottomVec);
  const angleComponent = clampNumber(angleSpan / Math.PI, 0, 1);
  const widthComponent = clampNumber(goalHeight / (goalHeight + distanceToGoal), 0, 1);
  const rawAngleQuality = clampNumber(0.6 * angleComponent + 0.4 * widthComponent, 0, 1);

  const distanceRatio = clampNumber(1 - Math.min(distanceToGoal, maxDistance) / maxDistance, 0, 1);
  const distanceQuality = Math.pow(distanceRatio, 0.85);

  const angleRangeModifier = 0.5 + 0.5 * distanceRatio;
  const angleQuality = clampNumber(rawAngleQuality * angleRangeModifier, 0, 1);

  const speedQualityBase = clampNumber(speed / idealSpeed, 0, 1);
  const speedQuality = clampNumber(speedQualityBase * (0.6 + 0.4 * distanceRatio), 0, 1);

  let nearestOpponent = 9999;
  let closeOpponents = 0;
  let facingPressure = 0;
  let laneBlockers = 0;
  const laneWidth = Math.max(goalHeight * 0.5, 50);
  const distanceLimit = Math.max(distanceToGoal + 120, goalHeight * 1.6);

  for (let i = 0; i < opponents.length; i += 1) {
    const opponent = opponents[i];
    const position = entityPosition(opponent);
    const offset = { x: position.x - ball.x, y: position.y - ball.y };
    const dist = safeHypot(offset.x, offset.y);
    if (!dist) continue;
    if (dist < nearestOpponent) nearestOpponent = dist;
    const proj = dot(offset, pathDir);
    const withinRadius = proj > -40 && dist <= 160;
    if (withinRadius) closeOpponents += 1;
    if (proj >= -30 && proj <= distanceLimit) {
      const lateralSquared = Math.max(0, dist * dist - proj * proj);
      if (lateralSquared <= laneWidth * laneWidth) laneBlockers += 1;
    }
    if (withinRadius && opponent.velocity) {
      const normVel = normalize(entityVelocity(opponent));
      if (normVel.length) {
        const toShooter = normalize({
          x: shooter.position.x - position.x,
          y: shooter.position.y - position.y
        });
        if (toShooter.length) {
          const facing = dot(normVel, toShooter);
          if (facing > 0.2) facingPressure += facing;
        }
      }
    }
  }

  const nearestPenalty = clampNumber(1 - Math.min(nearestOpponent, 260) / 260, 0, 1);
  const closePenalty = clampNumber(closeOpponents / 4, 0, 1);
  const facingPenalty = clampNumber(facingPressure / 3, 0, 1);

  const visibility = goalVisibility(ball, goal, pathDir, opponents);
  const interceptionRisk = estimateInterceptionRisk(
    ball,
    pathDir,
    distanceToGoal,
    speed || idealSpeed,
    opponents
  );
  // Preserve the historical pressure signals while giving the continuous
  // arrival-time estimate a modest influence.  The small weight is deliberate:
  // it should improve ordering for moving defenders without overwhelming a
  // well-calibrated distance/angle signal.
  const pressureComposite = clampNumber(
    0.45 * nearestPenalty + 0.3 * closePenalty + 0.1 * facingPenalty + 0.15 * interceptionRisk,
    0,
    1
  );
  const lanePenalty = clampNumber(laneBlockers / 2, 0, 1);

  const { laneCount: supportLane, closeCount: supportClose } = buildSupportStats(
    teammates,
    ball,
    pathDir,
    distanceLimit,
    laneWidth + 20
  );

  const laneSpace = clampNumber(1 - lanePenalty, 0, 1);
  const depthSpace = clampNumber(1 - nearestPenalty, 0, 1);
  const spaceRangeModifier = 0.4 + 0.6 * distanceRatio;
  // Goal-mouth visibility catches cases where a blocker is outside the broad
  // centre corridor but still covers a meaningful section of the target.
  const visibilityQuality = 0.65 + 0.35 * visibility.visibleFraction;
  const spaceQuality = clampNumber(
    (0.7 * laneSpace + 0.3 * depthSpace) * spaceRangeModifier * visibilityQuality,
    0,
    1
  );

  const supportLaneScore = clampNumber(supportLane / 2, 0, 1);
  const supportCloseScore = clampNumber(supportClose / 3, 0, 1);
  const supportRangeModifier = 0.5 + 0.5 * distanceRatio;
  const supportQuality = clampNumber((0.6 * supportLaneScore + 0.4 * supportCloseScore) * supportRangeModifier, 0, 1);

  const distancePenalty = clampNumber(distanceToGoal / (maxDistance * 0.9), 0, 1);

  const logisticFeatures = {
    angleQuality,
    distanceQuality,
    speedQuality,
    spaceQuality,
    supportQuality,
    pressureComposite,
    lanePenalty,
    distancePenalty
  };

  const detailFeatures = {
    modelVersion: SHOT_MODEL_VERSION,
    distanceToGoal,
    goalMouthAngle: angleSpan,
    goalMouthVisibleFraction: visibility.visibleFraction,
    goalMouthBlockedFraction: visibility.blockedFraction,
    goalMouthBlockerIntervals: visibility.intervals,
    interceptionRisk,
    angleQuality,
    distanceQuality,
    speedQuality,
    spaceQuality,
    supportQuality,
    pressureComposite,
    laneBlockers,
    supportLane,
    supportClose,
    nearestOpponent,
    closeOpponents,
    lanePenalty,
    distancePenalty
  };

  return {
    logisticFeatures,
    detailFeatures,
    meta: {
      modelVersion: SHOT_MODEL_VERSION,
      distanceToGoal,
      laneBlockers,
      supportLane,
      supportClose,
      nearestOpponent,
      closeOpponents,
      goalVisibleFraction: visibility.visibleFraction,
      interceptionRisk
    }
  };
}

export function buildShotFeatureVector(logisticFeatures) {
  return SHOT_FEATURE_CONFIG.map(({ key }) => Number.isFinite(logisticFeatures[key]) ? logisticFeatures[key] : 0);
}

function computeLogit(logisticFeatures) {
  let z = SHOT_LOGIT_BIAS;
  for (let i = 0; i < SHOT_FEATURE_CONFIG.length; i += 1) {
    const { key, weight } = SHOT_FEATURE_CONFIG[i];
    const value = Number.isFinite(logisticFeatures[key]) ? logisticFeatures[key] : 0;
    z += weight * value;
  }
  return z;
}

export function applyShotOutcomeAdjustments(xg, context) {
  let adjusted = xg;
  if (context.isOnTarget) adjusted = clampNumber(adjusted + 0.05, 0.05, 0.92);
  else if (context.isOffTarget) adjusted = clampNumber(adjusted * 0.45, 0.01, 0.3);
  else adjusted = clampNumber(adjusted, 0.02, 0.9);
  return adjusted;
}

const extractPreKickVelocity = context => {
  const candidate = context?.preKickVelocity
    || context?.ballPreKick?.velocity
    || context?.ballPreKick
    || context?.preShotBall?.velocity
    || context?.preShotBall;
  if (!candidate || typeof candidate !== 'object') return null;
  const vx = Number(candidate.vx ?? candidate.x);
  const vy = Number(candidate.vy ?? candidate.y);
  if (!Number.isFinite(vx) || !Number.isFinite(vy)) return null;
  return { vx, vy };
};

/**
 * Compute chance quality before the ball is struck.  Unlike the legacy
 * function this never inspects `isOnTarget`/`isOffTarget`, making it suitable
 * for evaluating chance creation and for training against goal outcomes.
 *
 * A calibration object can be passed directly or through
 * `context.xgCalibration`.  The returned `rawXg` is useful for fitting a new
 * calibration without recomputing replay geometry.
 */
export function computePreShotXg(context, options = {}) {
  if (!context) {
    return {
      xg: 0.05,
      preShotXg: 0.05,
      rawXg: 0.05,
      calibratedXg: 0.05,
      features: {},
      modelVersion: SHOT_MODEL_VERSION
    };
  }
  const preKickVelocity = extractPreKickVelocity(context);
  const sourceBall = context.ball || {};
  const preShotContext = {
    ...context,
    // Keep position and all defender state, but remove post-release velocity
    // unless the caller explicitly captured a pre-kick velocity.
    ball: {
      ...sourceBall,
      vx: preKickVelocity ? preKickVelocity.vx : 0,
      vy: preKickVelocity ? preKickVelocity.vy : 0
    },
    preShot: true,
    hasPreKickVelocity: Boolean(preKickVelocity)
  };
  const { logisticFeatures, detailFeatures, meta } = buildShotFeatureBundle(preShotContext);
  const modelLogit = computeLogit(logisticFeatures);
  const rawXg = clampNumber(sigmoid(modelLogit), 0, 1);
  const calibration = options.calibration || context.xgCalibration || context.calibration || null;
  const calibratedXg = calibrateShotProbability(rawXg, calibration);
  return {
    xg: calibratedXg,
    preShotXg: calibratedXg,
    rawXg,
    calibratedXg,
    logit: modelLogit,
    features: detailFeatures,
    meta,
    modelVersion: SHOT_MODEL_VERSION,
    calibration: calibration || null
  };
}

/**
 * Explicit calibrated API.  It is intentionally separate from
 * `computeShotXg`, whose `xg` field is kept backwards-compatible for existing
 * saved reports and UI snapshots.
 */
export function computeCalibratedShotXg(context, options = {}) {
  const calibration = options.calibration || context?.xgCalibration || XG_CALIBRATION_CANDIDATE;
  const result = computePreShotXg(context, { ...options, calibration });
  const outcomeAdjusted = applyShotOutcomeAdjustments(result.rawXg, context || {});
  const calibratedOutcomeXg = calibrateShotProbability(outcomeAdjusted, calibration);
  return {
    ...result,
    xg: calibratedOutcomeXg,
    calibratedOutcomeXg,
    // Keep this explicit: callers asking for pre-shot xG should not silently
    // receive a value altered by whether the recorded attempt hit the frame.
    preShotXg: result.calibratedXg
  };
}

export function computeShotXg(context) {
  if (!context) return { xg: 0.05, features: {}, modelVersion: SHOT_MODEL_VERSION };
  // Build the legacy path from the original context so historical reports
  // retain exactly the same release-velocity semantics.  The new pre-shot
  // result below deliberately strips that velocity to prevent training
  // leakage.
  const legacyBundle = buildShotFeatureBundle(context);
  const legacyRawXg = sigmoid(computeLogit(legacyBundle.logisticFeatures));
  const legacyXg = applyShotOutcomeAdjustments(legacyRawXg, context);
  const preShot = computePreShotXg(context);
  const calibration = context.xgCalibration || context.calibration || null;
  const calibratedOutcomeXg = calibrateShotProbability(legacyXg, calibration);
  const useCalibrated = context.useCalibratedXg === true || context.xgOutput === 'calibrated';
  return {
    // Preserve the historical value unless a caller explicitly opts into the
    // calibrated output.  New code should use computePreShotXg or
    // computeCalibratedShotXg and persist modelVersion with the result.
    xg: useCalibrated ? calibratedOutcomeXg : legacyXg,
    legacyXg,
    rawXg: preShot.rawXg,
    preShotXg: preShot.calibratedXg,
    calibratedXg: preShot.calibratedXg,
    calibratedOutcomeXg,
    logit: preShot.logit,
    features: preShot.features,
    meta: preShot.meta,
    modelVersion: SHOT_MODEL_VERSION,
    calibration: calibration || null
  };
}

export default computeShotXg;
